import PermissionButton from 'components/buttons/PermissionButton';
import { useInCreateDialog } from 'components/create-dialog/CreateDialogContext';
import { CreateDialogRequiredBlock } from 'components/create-dialog/CreateDialogRequired';
import { useDraftField, useDraftSaveMode } from 'components/drafts/DraftSaveMode';
import ContactPickerModal from 'components/forms/form-components/select-picker/ContactPickerModal';
import DateInput from 'components/forms/inputs/rhf-inputs/DateInput';
import DynamicMultiSelectInput from 'components/forms/inputs/rhf-inputs/DynamicMultiSelectInput';
import DynamicSelectInput from 'components/forms/inputs/rhf-inputs/DynamicSelectInput';
import HoursInput from 'components/forms/inputs/rhf-inputs/HoursInput';
import SelectInput from 'components/forms/inputs/rhf-inputs/SelectInput';
import TableMultiSelectInput from 'components/forms/inputs/rhf-inputs/TableMultiSelectInput';
import TextAreaInput from 'components/forms/inputs/rhf-inputs/TextAreaInput';
import { LockIcon } from 'components/icons';
import InfoTooltip from 'components/InfoTooltip';
import { useInRecordSection } from 'components/section-edit/SectionEdit';
import { FieldDescription } from 'components/ui/field';
import { ReactNode, useId } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { useLazyGetMinContactsWithRelationsListWithFiltersQuery } from 'store/api/contacts';
import { useLazyGetEmployeesListWithFiltersQuery } from 'store/api/employees';
import { useLazyGetMattersListQuery } from 'store/api/matters';
import { PartyType } from 'types/contacts';
import { EffortForm, RANKINGS_REFEREE_TYPE } from 'types/efforts';
import { getValidOptionsFromClassifier } from 'utils/formUtil';
import { getClassifierTextDescription, t } from 'utils/getTranslations';
import { renderMatterIndicators, renderMatterOption } from 'utils/matterOptionRenderer';
import { requireCurrentOrSpecifiedFields } from 'utils/validators';
import EffortExpenseList from '../expenses/EffortExpenseList';
import { EffortFormState } from './useEffortForm';

type FieldsProps = { form: EffortFormState };

const CREATE_CONTACT_PERMISSIONS = ['CREATE.PARTIES', 'CREATE.PARTIES.CLIENTS', 'CREATE.PARTIES.LEADS_AND_NON_CLIENTS'];

const LOCKED_NOTE_CLASS = 'text-caption text-fg-secondary new:text-muted-foreground -mt-2 flex items-start gap-1.5';

/** Why a field holds back a change, under it: the lock, then the sentence. */
const LockedNote = ({ id, children }: { id?: string; children: ReactNode }) => (
    <p id={id} className={LOCKED_NOTE_CLASS}>
        <LockIcon className="mt-px size-4 shrink-0" />
        <span>{children}</span>
    </p>
);

/** Overview: the day, the time spent, the kind of effort and what was done. */
export const OverviewFields = ({ form }: FieldsProps): JSX.Element => {
    const { control, setValue } = form.methods;
    const { classifiers, readOnlyUsedTimeInHours, onUsedTimeChange, hasRankingsReferees, onActivityTypeChange } = form;
    // On the record page the date and the time are each a cell of the card's grid, where the read view has its row.
    const inSection = useInRecordSection();
    const activityType = useWatch({ control, name: 'effortActivityType' });
    // Without the feature, an effort holding nominations keeps its type: another one would orphan them.
    const isTypeLocked = !!useWatch({ control, name: 'lockedRefereePartyIds' })?.length;
    const lockedReasonId = useId();
    // Rankings referee is offered only with the feature, though an effort that already has it still shows it.
    const activityTypes = (
        getValidOptionsFromClassifier(classifiers['EFFORT_ACTIVITY_TYPE']) as {
            label: string;
            value: string;
        }[]
    ).filter(({ value }) => hasRankingsReferees || value !== RANKINGS_REFEREE_TYPE || value === activityType?.value);
    return (
        <>
            <div className={inSection ? 'contents' : 'flex flex-wrap gap-4'}>
                <div className={inSection ? 'contents' : 'flex-1'}>
                    <DateInput control={control} name="effortDate" label="efforts.effort-date" required fullWidth />
                </div>
                <div className={inSection ? 'contents' : 'flex-1'}>
                    <HoursInput
                        control={control}
                        name="usedTimeInHours"
                        label="efforts.used-time"
                        placeholder="efforts.used-time"
                        required
                        readOnlyField={readOnlyUsedTimeInHours}
                        onChange={onUsedTimeChange}
                        useIncrementDecrementButtons
                        setValue={setValue}
                    />
                </div>
            </div>
            <SelectInput
                setValue={setValue}
                control={control}
                label="efforts.activity-type"
                placeholder="efforts.select-type"
                name="effortActivityType"
                options={activityTypes}
                getOptionLabel="label"
                getOptionIdentifier="value"
                // The options carry no description, and the classifier gives a value with none its code back: no line.
                getOptionDescription={({ value }) => {
                    const description = getClassifierTextDescription(classifiers['EFFORT_ACTIVITY_TYPE'], value);
                    return description === value ? '' : description;
                }}
                showOptionDescription
                lockedReasonId={isTypeLocked ? lockedReasonId : undefined}
                onChange={onActivityTypeChange}
                required
            />
            {isTypeLocked ? (
                <LockedNote id={lockedReasonId}>
                    <FormattedMessage id="efforts.nominations.type-locked" />
                </LockedNote>
            ) : null}
            <TextAreaInput
                label="efforts.description"
                control={control}
                name="description"
                placeholder="efforts.description"
                tooltip={<InfoTooltip text="efforts.tooltips.description" />}
                maxRows={5}
                minRows={5}
            />
        </>
    );
};

/** People involved: who made the effort, and who else took part. */
export const PeopleFields = ({ form }: FieldsProps): JSX.Element => {
    const { control, setValue } = form.methods;
    return (
        <>
            <DynamicSelectInput
                setValue={setValue}
                control={control}
                label="efforts.effort-by-employee"
                placeholder="forms.select-employee"
                name="effortByEmployee"
                paginated={{
                    fetch: useLazyGetEmployeesListWithFiltersQuery,
                    searchField: 'fullName',
                }}
                getOptionLabel="name"
                getOptionIdentifier="id"
                required
            />
            <DynamicMultiSelectInput
                setValue={setValue}
                control={control}
                label="efforts.participants"
                placeholder="efforts.participants"
                name="employees"
                paginated={{
                    fetch: useLazyGetEmployeesListWithFiltersQuery,
                    searchField: 'fullName',
                }}
                getOptionLabel="name"
                getOptionIdentifier="id"
            />
        </>
    );
};

/**
 * Linked entities: the people and companies the effort was for, one of which is required (a person, for a Rankings
 * referee effort), a person adding their employers unless the type is Rankings referee, and the cases it resulted in.
 */
export const LinkedEntitiesFields = ({ form }: FieldsProps): JSX.Element => {
    const { control, setValue, trigger } = form.methods;
    const {
        selectedPartyPersonIds,
        selectedPartyCompanyIds,
        groupEffortForEntities,
        groupEffortForPersons,
        maybePrefillEffortForEntities,
        openAddNewContactModal,
        openAddNewCompanyContactModal,
        hasRankingsReferees,
        refereeRefusal,
        onPartyPersonsChange,
    } = form;
    // The pair's rule is a required one, off while the effort is saved as a draft.
    const drafting = useDraftSaveMode();
    // Referees are people: a Rankings referee effort needs one in Effort for people, whatever else it is for.
    const needsPeople = useWatch({ control, name: 'effortActivityType' })?.value === RANKINGS_REFEREE_TYPE;
    const hasLockedReferees = !!useWatch({ control, name: 'lockedRefereePartyIds' })?.length;
    // In the create dialog each list of contacts takes the grid's whole width, as the training form's invitees do.
    const fullWidth = useInCreateDialog() ? 'col-span-full' : undefined;
    const hasNoContact = selectedPartyPersonIds.length === 0 && selectedPartyCompanyIds.length === 0;
    // Neither input is starred, so a draft save is told of the pair here, as the one field it is.
    useDraftField(`${t('efforts.for-persons')} / ${t('efforts.for-entities')}`, true, hasNoContact);
    const requireOne = (value: unknown, formValues: EffortForm, other: 'partyPersons' | 'partyCompanies') =>
        drafting ||
        requireCurrentOrSpecifiedFields(
            value,
            formValues,
            [other],
            t('validation.requires-at-least-one-of-fields', {
                fields: [t('efforts.for-entities'), t('efforts.for-persons')].join(', '),
            })
        );
    return (
        <>
            {/* One of the two is required, whichever: the create dialog draws the pair in Required, filled or not,
                and counts it as one field, left to fill until either holds a contact. */}
            <CreateDialogRequiredBlock when isEmpty={hasNoContact}>
                <TableMultiSelectInput
                    setValue={setValue}
                    control={control}
                    label="efforts.for-persons"
                    placeholder="efforts.search-person"
                    name="partyPersons"
                    required={needsPeople}
                    wrapperClassName={fullWidth}
                    paginated={{
                        fetch: useLazyGetMinContactsWithRelationsListWithFiltersQuery,
                        filters: `partyType==${PartyType.PERSON}`,
                        searchField: ['name', 'partyNumber'],
                        sort: 'name,asc',
                        additionalParams:
                            selectedPartyCompanyIds.length > 0
                                ? { prioritizeEmployeesOf: selectedPartyCompanyIds }
                                : undefined,
                    }}
                    isPickerModalOptionEnabled={(option) => option.partyType === PartyType.PERSON}
                    PickerModalComponent={ContactPickerModal}
                    groupByStaticFields={groupEffortForPersons}
                    onChange={(people) => {
                        onPartyPersonsChange(people);
                        maybePrefillEffortForEntities();
                        trigger('partyCompanies', { shouldFocus: true });
                        trigger('partyPersons', { shouldFocus: true });
                    }}
                    getOptionLabel="name"
                    getOptionIdentifier="id"
                    disableCloseOnSelect
                    noOptionsContent={
                        <PermissionButton
                            permissions={CREATE_CONTACT_PERMISSIONS}
                            variant="contained"
                            color="secondary"
                            size="extraSmall"
                            onClick={openAddNewContactModal}
                        >
                            <FormattedMessage id="contacts.add-new-contact" />
                        </PermissionButton>
                    }
                    validate={(value, formValues) => requireOne(value, formValues, 'partyCompanies')}
                />
                {needsPeople && hasRankingsReferees ? (
                    <FieldDescription className="-mt-2 text-xs">
                        <FormattedMessage id="efforts.nominations.people-hint" />
                    </FieldDescription>
                ) : null}
                {/* Mounted empty, so the refusal of a removal is said as it happens. */}
                {hasLockedReferees ? (
                    <div role="status" className="sr-only">
                        {refereeRefusal}
                    </div>
                ) : null}
                {refereeRefusal ? <LockedNote>{refereeRefusal}</LockedNote> : null}
                <TableMultiSelectInput
                    setValue={setValue}
                    control={control}
                    label="efforts.for-entities"
                    placeholder="efforts.search-company"
                    name="partyCompanies"
                    wrapperClassName={fullWidth}
                    paginated={{
                        fetch: useLazyGetMinContactsWithRelationsListWithFiltersQuery,
                        filters: `partyType==${PartyType.COMPANY}`,
                        searchField: ['name', 'partyNumber'],
                        sort: 'name,asc',
                        additionalParams:
                            selectedPartyPersonIds.length > 0
                                ? { prioritizeCompaniesOf: selectedPartyPersonIds }
                                : undefined,
                    }}
                    isPickerModalOptionEnabled={(option) => option.partyType === PartyType.COMPANY}
                    PickerModalComponent={ContactPickerModal}
                    groupByStaticFields={groupEffortForEntities}
                    onChange={() => {
                        trigger('partyPersons', { shouldFocus: true });
                        trigger('partyCompanies', { shouldFocus: true });
                    }}
                    getOptionLabel="name"
                    getOptionIdentifier="id"
                    disableCloseOnSelect
                    noOptionsContent={
                        <PermissionButton
                            permissions={CREATE_CONTACT_PERMISSIONS}
                            variant="contained"
                            color="secondary"
                            size="extraSmall"
                            onClick={openAddNewCompanyContactModal}
                        >
                            <FormattedMessage id="contacts.add-new-contact" />
                        </PermissionButton>
                    }
                    validate={(value, formValues) => requireOne(value, formValues, 'partyPersons')}
                />
                {needsPeople && hasRankingsReferees ? (
                    <FieldDescription className="-mt-2 text-xs">
                        <FormattedMessage id="efforts.nominations.entities-hint" />
                    </FieldDescription>
                ) : null}
            </CreateDialogRequiredBlock>
            <DynamicMultiSelectInput
                setValue={setValue}
                control={control}
                label="efforts.resulted-in-cases"
                placeholder="efforts.resulted-in-cases"
                name="matters"
                paginated={{
                    fetch: useLazyGetMattersListQuery,
                    searchField: ['fullName', 'clientParty.name'],
                    sort: ['clientParty.name,asc', 'name,asc'],
                }}
                groupByField="clientParty.name"
                getOptionLabel="fullName"
                getOptionIdentifier="id"
                getSelectedValueStartAdornment={renderMatterIndicators}
                customOptionRenderer={renderMatterOption}
            />
        </>
    );
};

/** Expenses: the effort's expense lines, each added or edited in the expense modal. */
export const ExpensesFields = ({ form }: FieldsProps): JSX.Element => (
    <Controller
        control={form.methods.control}
        name="expenses"
        render={({ field }) => (
            <EffortExpenseList
                expenseFormDataList={form.expenseFormDataList}
                setExpenseFormDataList={form.setExpenseFormDataList}
                classifiers={form.classifiers}
                onChange={field.onChange}
            />
        )}
    />
);
